import { useCallback, useMemo, useState } from "react";
import { OutstandingCashItem } from "@common/contracts/reconciliation.contract";
import { ChildrenProps } from "../../types";
import {
    CheckMergeSelection,
    getCheckMergeTargetKey,
    ReconciliationContext,
    TransactionToMatch,
} from "./Reconciliation.context";

export const ReconciliationContextProvider: React.FC<ChildrenProps> = ({
    children,
}) => {
    const [
        financialTransactionsToRemoveIds,
        setFinancialTransactionsToRemoveIds,
    ] = useState<number[]>([]);
    const [statementTransactionsToAddIds, setStatementTransactionsToAddIds] =
        useState<string[]>([]);
    const [
        notReconciledFinancialTransactionsIds,
        setNotReconciledFinancialTransactionsIds,
    ] = useState<number[]>([]);
    const [
        financialTransactionIdsIgnoringDateChanges,
        setFinancialTransactionIdsIgnoringDateChanges,
    ] = useState<number[]>([]);
    const [
        financialTransactionsFromPreviousPeriodIds,
        setFinancialTransactionsFromPreviousPeriodIds,
    ] = useState<number[]>([]);
    const [transactionToMatch, setTransactionToMatch] =
        useState<TransactionToMatch>();
    const [checkToMatch, setCheckToMatch] = useState<OutstandingCashItem>();
    const [checkMerges, setCheckMerges] = useState<CheckMergeSelection[]>([]);
    const [isReconciliationComplete, setIsReconciliationComplete] =
        useState(false);

    const resetReconciliationState = useCallback(() => {
        setFinancialTransactionsToRemoveIds([]);
        setStatementTransactionsToAddIds([]);
        setNotReconciledFinancialTransactionsIds([]);
        setFinancialTransactionIdsIgnoringDateChanges([]);
        setFinancialTransactionsFromPreviousPeriodIds([]);
        setTransactionToMatch(undefined);
        setCheckToMatch(undefined);
        setCheckMerges([]);
        setIsReconciliationComplete(false);
    }, []);

    const unregisterCheckMerge = useCallback((checkTransactionId: number) => {
        setCheckMerges((prev) =>
            prev.filter(
                (merge) => merge.checkTransactionId !== checkTransactionId,
            ),
        );
    }, []);
    const registerCheckMerge = useCallback((selection: CheckMergeSelection) => {
        // A check clears exactly once and a line clears one check, so a new
        // pick replaces any pairing it would contradict.
        setCheckMerges((prev) => [
            ...prev.filter(
                (merge) =>
                    merge.checkTransactionId !== selection.checkTransactionId &&
                    getCheckMergeTargetKey(merge) !==
                        getCheckMergeTargetKey(selection),
            ),
            selection,
        ]);
        setNotReconciledFinancialTransactionsIds((prev) =>
            prev.filter((id) => id !== selection.checkTransactionId),
        );
    }, []);

    const registerFinancialTransactionToRemove = useCallback((id: number) => {
        setFinancialTransactionsToRemoveIds((prev) => [...prev, id]);
    }, []);

    const registerStatementTransactionToAdd = useCallback((id: string) => {
        setStatementTransactionsToAddIds((prev) => [...prev, id]);
    }, []);

    const unregisterFinancialTransactionToRemove = useCallback((id: number) => {
        setFinancialTransactionsToRemoveIds((prev) =>
            prev.filter((i) => i !== id),
        );
    }, []);

    const unregisterStatementTransactionToAdd = useCallback((id: string) => {
        setStatementTransactionsToAddIds((prev) =>
            prev.filter((i) => i !== id),
        );
    }, []);

    const registerNotReconciledFinancialTransaction = useCallback(
        (id: number) => {
            setNotReconciledFinancialTransactionsIds((prev) => [...prev, id]);
        },
        [],
    );

    const unregisterNotReconciledFinancialTransaction = useCallback(
        (id: number) => {
            setNotReconciledFinancialTransactionsIds((prev) =>
                prev.filter((i) => i !== id),
            );
        },
        [],
    );

    const registerFinancialTransactionIdIgnoringDateChange = useCallback(
        (id: number) => {
            setFinancialTransactionIdsIgnoringDateChanges((prev) => [
                ...prev,
                id,
            ]);
        },
        [],
    );

    const unregisterFinancialTransactionIdIgnoringDateChange = useCallback(
        (id: number) => {
            setFinancialTransactionIdsIgnoringDateChanges((prev) =>
                prev.filter((i) => i !== id),
            );
        },
        [],
    );

    const registerFinancialTransactionFromPreviousPeriod = useCallback(
        (id: number) => {
            setFinancialTransactionsFromPreviousPeriodIds((prev) => [
                ...prev,
                id,
            ]);
        },
        [],
    );

    const unregisterFinancialTransactionFromPreviousPeriod = useCallback(
        (id: number) => {
            setFinancialTransactionsFromPreviousPeriodIds((prev) =>
                prev.filter((i) => i !== id),
            );
        },
        [],
    );

    const financialTransactionsToRemoveIdsSet = useMemo(
        () => new Set(financialTransactionsToRemoveIds),
        [financialTransactionsToRemoveIds],
    );

    const statementTransactionsToAddIdsSet = useMemo(
        () => new Set(statementTransactionsToAddIds),
        [statementTransactionsToAddIds],
    );

    const notReconciledFinancialTransactionsIdsSet = useMemo(
        () => new Set(notReconciledFinancialTransactionsIds),
        [notReconciledFinancialTransactionsIds],
    );

    const financialTransactionIdsIgnoringDateChangesSet = useMemo(
        () => new Set(financialTransactionIdsIgnoringDateChanges),
        [financialTransactionIdsIgnoringDateChanges],
    );

    const value = useMemo(
        () => ({
            financialTransactionsToRemoveIds,
            financialTransactionsToRemoveIdsSet,
            statementTransactionsToAddIds,
            statementTransactionsToAddIdsSet,
            notReconciledFinancialTransactionsIds,
            notReconciledFinancialTransactionsIdsSet,
            financialTransactionIdsIgnoringDateChanges,
            financialTransactionIdsIgnoringDateChangesSet,
            financialTransactionsFromPreviousPeriodIds,
            transactionToMatch,
            checkToMatch,
            checkMerges,
            isReconciliationComplete,
            registerFinancialTransactionToRemove,
            registerStatementTransactionToAdd,
            unregisterFinancialTransactionToRemove,
            unregisterStatementTransactionToAdd,
            setTransactionToMatch,
            setCheckToMatch,
            registerCheckMerge,
            unregisterCheckMerge,
            replaceCheckMerges: setCheckMerges,
            setIsReconciliationComplete,
            registerNotReconciledFinancialTransaction,
            unregisterNotReconciledFinancialTransaction,
            registerFinancialTransactionIdIgnoringDateChange,
            unregisterFinancialTransactionIdIgnoringDateChange,
            registerFinancialTransactionFromPreviousPeriod,
            unregisterFinancialTransactionFromPreviousPeriod,
            resetReconciliationState,
        }),
        [
            financialTransactionsToRemoveIds,
            financialTransactionsToRemoveIdsSet,
            statementTransactionsToAddIds,
            statementTransactionsToAddIdsSet,
            notReconciledFinancialTransactionsIds,
            notReconciledFinancialTransactionsIdsSet,
            financialTransactionIdsIgnoringDateChanges,
            financialTransactionIdsIgnoringDateChangesSet,
            financialTransactionsFromPreviousPeriodIds,
            transactionToMatch,
            checkToMatch,
            checkMerges,
            isReconciliationComplete,
            registerFinancialTransactionToRemove,
            registerStatementTransactionToAdd,
            unregisterFinancialTransactionToRemove,
            unregisterStatementTransactionToAdd,
            registerCheckMerge,
            unregisterCheckMerge,
            setCheckMerges,
            registerNotReconciledFinancialTransaction,
            unregisterNotReconciledFinancialTransaction,
            registerFinancialTransactionIdIgnoringDateChange,
            unregisterFinancialTransactionIdIgnoringDateChange,
            registerFinancialTransactionFromPreviousPeriod,
            unregisterFinancialTransactionFromPreviousPeriod,
            resetReconciliationState,
        ],
    );

    return (
        <ReconciliationContext.Provider value={value}>
            {children}
        </ReconciliationContext.Provider>
    );
};
